import React, { useMemo, useState } from "react";
import {
  Box,
  Paper,
  Toolbar,
  Typography,
  Stack,
  Button,
  Snackbar,
  Grid,
  Chip,
  TextField,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

const isBacklogStatus = (row) => {
  const status = String(row?.status || "").toUpperCase();
  const grade = String(row?.grade || "").toUpperCase();
  const attendance = String(row?.attendance || "").toUpperCase();
  return (
    status === "FAIL" ||
    status === "ABSENT" ||
    status === "AB" ||
    grade === "AB" ||
    attendance === "AB"
  );
};

function StudentBacklogCheck() {
  const [rollno, setRollno] = useState("");
  const [studentResults, setStudentResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [student, setStudent] = useState(null);
  const [searched, setSearched] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [totalBacklogs, setTotalBacklogs] = useState(0);

  const user = useMemo(() => {
    try {
      const storedUser = sessionStorage.getItem("user");
      return storedUser ? JSON.parse(storedUser) : null;
    } catch (err) {
      return null;
    }
  }, []);
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const showSnackbar = (msg, sev = "success") => {
    setSnackbarMessage(msg);
    setSnackbarSeverity(sev);
    setSnackbarOpen(true);
  };

  const getAuthHeaders = () => {
    const token = sessionStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const formatExamMonthYear = (month, year) => {
    if (!month) return "";
    const monthText = String(month);
    const yearText = year ? String(year) : "";
    return yearText && !monthText.includes(yearText) ? `${monthText}-${yearText}` : monthText;
  };

  const rows = useMemo(() => {
    return studentResults.map((row, index) => ({
      id: row.result_id || `${row.course_code || "course"}-${index}`,
      study_year: row.year_number || "",
      semester: row.semester_number || "",
      exam_type: row.exam_type_name || "",
      month_year: formatExamMonthYear(row.exam_month || row.passed_month, row.passed_year),
      course_code: row.course_code || "",
      course_name: row.course_name || "",
      internal:
        row.internal_marks !== undefined && row.internal_marks !== null
          ? Number(row.internal_marks).toFixed(0)
          : "-",
      external:
        row.external_marks !== undefined && row.external_marks !== null
          ? Number(row.external_marks).toFixed(0)
          : "-",
      total:
        row.total_marks !== undefined && row.total_marks !== null
          ? Number(row.total_marks).toFixed(0)
          : "-",
      credits:
        row.credits !== undefined && row.credits !== null ? Number(row.credits).toFixed(0) : "-",
      grade: row.grade || "",
      grade_points:
        row.grade_point !== undefined && row.grade_point !== null
          ? Number(row.grade_point).toFixed(0)
          : "-",
      status: row.status || "",
    }));
  }, [studentResults]);

  const groupedByYearSemester = useMemo(() => {
    const groups = new Map();
    rows.forEach((row) => {
      const key = `${row.study_year}-${row.semester}`;
      if (!groups.has(key)) {
        groups.set(key, { year: row.study_year, semester: row.semester, data: [] });
      }
      groups.get(key).data.push(row);
    });
    return Array.from(groups.values());
  }, [rows]);

  const columns = [
    {
      field: "id",
      headerName: "S.No",
      width: 80,
      valueGetter: (params) => params.api.getRowIndex(params.id) + 1,
    },
    { field: "study_year", headerName: "Year", width: 110 },
    { field: "semester", headerName: "Semester", width: 110 },
    { field: "exam_type", headerName: "ExamType", width: 120 },
    { field: "month_year", headerName: "MonthYear", width: 160 },
    { field: "course_code", headerName: "Course Code", width: 130 },
    { field: "course_name", headerName: "Course Name", flex: 1.4, minWidth: 230 },
    {
      field: "internal",
      headerName: "Internal",
      width: 90,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "external",
      headerName: "External",
      width: 90,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "total",
      headerName: "Total",
      width: 90,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "credits",
      headerName: "Credits",
      width: 80,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "grade",
      headerName: "Grade",
      width: 80,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "grade_points",
      headerName: "Grade Points",
      width: 110,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "status",
      headerName: "Status",
      width: 100,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Chip
          label={params.value || "-"}
          size="small"
          sx={{
            color: isBacklogStatus(params.row) ? "#d32f2f" : "#000",
            fontWeight: 800,
            borderRadius: 1,
            backgroundColor: isBacklogStatus(params.row) ? "#ffebee" : "#f2f4f7",
          }}
        />
      ),
    },
  ];

  const handleGetBacklogs = async () => {
    const trimmedRollNo = rollno.trim();
    if (!trimmedRollNo) {
      showSnackbar("Please enter student roll no", "warning");
      return;
    }
    if (!user?.client_id || !user?.college_id) {
      showSnackbar("Client or college details not found", "error");
      return;
    }

    setLoading(true);
    setSearched(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/exam-backlogs/student-backlog`, {
        headers: getAuthHeaders(),
        params: {
          client_id: user.client_id,
          college_id: user.college_id,
          student_rollno: trimmedRollNo,
        },
      });
      const data = response.data?.data || {};
      const backlogRows = data.failed_rows || [];

      setStudent(data.student || null);
      setStudentResults(backlogRows);
      setTotalBacklogs(Number(data.total_backlogs ?? backlogRows.length) || 0);

      if (!data.student) {
        showSnackbar("Student not found", "warning");
      } else if (!backlogRows.length) {
        showSnackbar("No backlog subjects found for this student", "info");
      }
    } catch (err) {
      setStudent(null);
      setStudentResults([]);
      setTotalBacklogs(0);
      showSnackbar(err.response?.data?.message || "Error fetching student backlogs", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = () => {
    const trimmedRollNo = rollno.trim();
    if (!student || !trimmedRollNo || totalBacklogs === 0) {
      showSnackbar("No backlog data to download", "warning");
      return;
    }

    setDownloading(true);
    const params = new URLSearchParams({
      client_id: user.client_id,
      college_id: user.college_id,
      student_rollno: trimmedRollNo,
    });

    window.open(
      `${API_BASE_URL}/api/exam-backlogs/student-backlog/download-pdf?${params}`,
      "_blank"
    );
    setDownloading(false);
  };

  return (
    <Box p={2} sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
      <Paper
        elevation={1}
        sx={{
          width: "100%",
          maxWidth: 1250,
          margin: "0 auto",
          overflowX: "auto",
          boxSizing: "border-box",
        }}
      >
        <Toolbar sx={{ gap: 1, flexWrap: "wrap" }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            STUDENT BACKLOG CHECK
          </Typography>
          {student && (
            <Chip
              label={`Total Backlogs: ${totalBacklogs}`}
              sx={{
                color: totalBacklogs ? "#c62828" : "#14883e",
                backgroundColor: totalBacklogs ? "#fdeaea" : "#e7f6ec",
                fontWeight: 900,
                borderRadius: 1,
                fontSize: 14,
                height: 34,
              }}
            />
          )}
        </Toolbar>
        <Box p={2} sx={{ width: "100%", boxSizing: "border-box" }}>
          <Stack spacing={2}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Student Roll No"
                  value={rollno}
                  onChange={(e) => setRollno(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleGetBacklogs();
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  onClick={handleGetBacklogs}
                  disabled={loading}
                  sx={{ color: "#fff", "&:hover": { color: "#fff" }, minHeight: 42 }}
                >
                  {loading ? "Loading..." : "Get Student Backlog List"}
                </Button>
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<PictureAsPdfIcon />}
                  onClick={handleDownloadPDF}
                  disabled={!student || !totalBacklogs || downloading}
                  sx={{
                    bgcolor: "#dc3545",
                    "&:hover": { bgcolor: "#c82333" },
                    color: "#fff",
                    minHeight: 42,
                  }}
                >
                  Download PDF
                </Button>
              </Grid>
              <Grid item xs={12} md={3}>
                {student && (
                  <Typography variant="body2" sx={{ color: "#243047", fontWeight: 700 }}>
                    {student.student_name || "-"} | Roll No: {student.student_rollno || "-"}
                  </Typography>
                )}
              </Grid>
            </Grid>

            {searched && student === null ? (
              <Typography variant="h6" color="error">
                Student not found.
              </Typography>
            ) : (
              <>
                {groupedByYearSemester.map((group) => (
                  <Box key={`${group.year}-${group.semester}`} sx={{ mt: 2 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        color: "#1f2a3d",
                        fontWeight: 700,
                        mb: 2,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <Box component="span" sx={{ fontSize: 24 }}>
                        📊
                      </Box>
                      {group.year} Year, {group.semester} Semester Backlog Details
                    </Typography>
                    <DataGrid
                      rows={group.data}
                      columns={columns}
                      autoHeight
                      loading={loading}
                      disableRowSelectionOnClick
                      hideFooter
                      sx={{
                        width: "auto",
                        minWidth: 1450,
                        border: "1px solid #dce3ec",
                        borderRadius: 1,
                        background: "#fff",
                        "& .MuiDataGrid-columnHeaders": {
                          backgroundColor: "#eef3f8",
                          color: "#1f2a3d",
                          fontWeight: 800,
                        },
                        "& .MuiDataGrid-columnHeaderTitle": {
                          fontWeight: 800,
                        },
                      }}
                    />
                  </Box>
                ))}
                <Typography variant="subtitle2" sx={{ mt: 2 }}>
                  <b>Note:</b> In case of any deviation, the concerned students may contact the COE
                  (Examination Cell) immediately.
                </Typography>
              </>
            )}
          </Stack>
        </Box>
      </Paper>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
}

export default StudentBacklogCheck;
